/**
 * FoodPulseRecipesCategory — landing for /food-pulse/recipes/:hubSlug
 *
 * Reuses the same recipe-card layout as the /recipes hub but filters
 * server-side by the `hub_slug` field on the seeded recipe docs. Any
 * category not represented in the seed still gets a "coming soon"
 * empty state so the sub-nav links never dead-end.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Users, ChefHat, UtensilsCrossed, PlayCircle, ExternalLink } from "lucide-react";

import SeoHead from "@/components/SeoHead";
import FoodPulseSlogan from "@/components/FoodPulseSlogan";
import { findFpSection } from "@/config/foodPulseSections";

const API = process.env.REACT_APP_BACKEND_URL;

// Match the hub-seed slug map so the URL param → human label + intro copy.
const HUB_LABELS = {
  breakfast:     { label: "Breakfast",             intro: "Morning classics from every cuisine — pancakes, congee, shakshuka, idli, huevos rancheros." },
  lunch:         { label: "Lunch",                 intro: "Bright, satisfying midday plates that leave room for the afternoon." },
  dinner:        { label: "Dinner",                intro: "Family dinners, one-pan meals, and slow-cooked classics for every table." },
  desserts:      { label: "Desserts",              intro: "Cakes, custards, halwa, mochi and every sweet ending in between." },
  soups:         { label: "Soups",                 intro: "Broths, chowders, dal, pho — one bowl, endless comfort." },
  salads:        { label: "Salads",                intro: "Bright greens, seasonal fruit, whole grains, warm dressings." },
  vegetarian:    { label: "Vegetarian",            intro: "Plant-forward recipes without compromise — packed with flavour and protein." },
  vegan:         { label: "Vegan",                 intro: "Entirely plant-based recipes from every cuisine — no animal products anywhere." },
  "gluten-free": { label: "Gluten Free",           intro: "Naturally gluten-free favourites and creative alternatives to wheat." },
  quick:         { label: "Quick Meals",           intro: "20 minutes or less, start to plate." },
  holiday:       { label: "Holiday Recipes",       intro: "Diwali sweets, Thanksgiving mains, Christmas roasts, Lunar New Year dumplings." },
  regional:      { label: "Regional Cuisine",      intro: "Recipes anchored to a specific place — Kochi to Kentucky." },
  international: { label: "International Cuisine", intro: "A global sampler — recipes from every continent." },
  // Cuisine tabs
  indian:        { label: "Indian Cuisine",        intro: "Authentic Indian dishes across all 28 states — Punjabi, Bengali, Tamil, Maharashtrian and more." },
  chinese:       { label: "Chinese Cuisine",       intro: "Sichuan heat, Cantonese balance, Shanghainese elegance — the eight great traditions." },
  italian:       { label: "Italian Cuisine",       intro: "Pasta, pizza, risotto — regional Italian cooking from Sicily to Tuscany." },
  thai:          { label: "Thai Cuisine",          intro: "Sweet, sour, salty, spicy — Thailand's four-flavour balancing act." },
  japanese:      { label: "Japanese Cuisine",      intro: "Ramen, sushi, izakaya small plates and everyday washoku." },
  korean:        { label: "Korean Cuisine",        intro: "Banchan, bulgogi, kimchi jjigae — the deep umami of Korean home cooking." },
  french:        { label: "French Cuisine",        intro: "Bistro classics, provincial stews, patisserie — the mother sauces and beyond." },
  greek:         { label: "Greek Cuisine",         intro: "Olive oil, feta, lemon and herbs — Mediterranean cooking at its heart." },
  lebanese:      { label: "Lebanese Cuisine",      intro: "Mezze, kibbeh, tabbouleh — the generosity of the Levantine table." },
  kerala:        { label: "Kerala Cuisine",        intro: "God's own country on a plate — appam, meen curry, avial, sadya." },
  "sri-lankan":  { label: "Sri Lankan Cuisine",    intro: "Rice & curry, hoppers, kottu, string hoppers — the fire of the island." },
  punjabi:       { label: "Punjabi Cuisine",       intro: "Butter chicken, sarson da saag, makki di roti, chole bhature — Punjab's hearty heart." },
  goan:          { label: "Goan Cuisine",          intro: "Vindaloo, sorpotel, xacuti — Portuguese-Konkan fusion by the Arabian Sea." },
  gujarati:      { label: "Gujarati Cuisine",      intro: "Thali culture — dhokla, undhiyu, khandvi, farsan and the sweet-sour balance." },
};

function StarRow({ avg, count }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
      <span style={{ color: "#c47f3a" }}>★</span> {avg?.toFixed(1) || "—"} ({count || 0})
    </span>
  );
}

function RecipeCard({ r }) {
  const totalMin = r.total_time_min || (r.prep_time_min || 0) + (r.cook_time_min || 0);
  const blurb = (r.ai_summary || r.short_description || "").trim();
  const hasVideo = !!(r.video_url || r.video_search_url);
  return (
    <Link
      to={`/recipes/${r.slug}`}
      data-testid={`fp-recipe-card-${r.slug}`}
      className="flex flex-col rounded-lg overflow-hidden border transition hover:shadow-lg hover:-translate-y-0.5"
      style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-cream, #FDFBF7)" }}
    >
      <div className="w-full aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center overflow-hidden relative">
        {r.cover_photo ? (
          <img
            src={r.cover_photo}
            alt={r.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <UtensilsCrossed size={48} style={{ color: "var(--wofy-primary)" }} />
        )}
        {hasVideo && (
          <span
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}
          >
            <PlayCircle size={11} /> Video
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1 gap-2">
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
          <span>{r.cuisine}</span>
          <span>·</span>
          <span>{r.country}</span>
        </div>
        <h3 className="font-display text-lg leading-snug">{r.title}</h3>
        {blurb && (
          <p className="text-xs leading-relaxed line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
            {blurb.length > 150 ? blurb.slice(0, 150).trimEnd() + "…" : blurb}
          </p>
        )}
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {totalMin || 0} min</span>
          <span className="inline-flex items-center gap-1"><Users size={11} /> {r.servings || 4}</span>
          <span className="inline-flex items-center gap-1"><ChefHat size={11} /> {r.difficulty}</span>
          <StarRow avg={r.rating_avg || 0} count={r.rating_count || 0} />
        </div>
      </div>
    </Link>
  );
}

export default function FoodPulseRecipesCategory() {
  const { hubSlug } = useParams();
  const meta = HUB_LABELS[hubSlug] || { label: hubSlug, intro: "" };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const parent = useMemo(() => findFpSection("recipes"), []);

  useEffect(() => {
    document.title = `${meta.label} — Food Pulse Recipes`;
  }, [meta.label]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/recipes`, {
          params: { hub_slug: hubSlug, limit: 60, sort: "new" },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hubSlug]);

  const siblings = (parent?.children || []).filter((c) => c.to !== `/food-pulse/recipes/${hubSlug}`);

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid={`fp-recipes-cat-${hubSlug}`}>
      <SeoHead
        section="food-pulse"
        title={`${meta.label} — Food Pulse Recipes`}
        description={meta.intro || `Authentic ${meta.label} recipes with videos, ingredients, and step-by-step cooking guides.`}
      />

      {/* Hero */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="fp-recipes-cat-hero">
        <div className="max-w-6xl mx-auto px-4 py-10 md:py-14">
          <Link
            to="/food-pulse/recipes"
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest mb-3 hover:underline"
            style={{ color: "#8B5E10" }}
            data-testid="fp-recipes-cat-back"
          >
            <ArrowLeft className="w-3 h-3" /> Food Pulse · Recipes
          </Link>
          <h1 className="font-display text-3xl md:text-5xl leading-tight" style={{ color: "#4A2E0F" }}>
            {meta.label}
          </h1>
          {meta.intro && (
            <p className="mt-3 max-w-2xl text-sm md:text-base" style={{ color: "#6B4A16" }}>{meta.intro}</p>
          )}
          <FoodPulseSlogan surface="recipes" className="mt-4" testId="fp-recipes-cat-slogan" />
          <div className="mt-4 text-xs opacity-70" data-testid="fp-recipes-cat-count">
            {loading ? "Loading…" : `${total.toLocaleString()} recipe${total === 1 ? "" : "s"}`}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="fp-recipes-cat-loading">Loading recipes…</div>
        ) : items.length === 0 ? (
          <div
            className="rounded-2xl border p-10 text-center"
            style={{ borderColor: "#C79A3F", background: "#fff" }}
            data-testid="fp-recipes-cat-empty"
          >
            <ChefHat className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
            <div className="font-display text-lg mb-1">No {meta.label.toLowerCase()} recipes yet.</div>
            <div className="text-sm opacity-70">
              We're actively seeding this category — check back shortly, or explore the full recipes hub.
            </div>
            <Link
              to="/food-pulse/recipes"
              className="inline-flex mt-4 text-xs font-semibold px-3 py-1.5 rounded-full border hover:bg-black/[0.02]"
              style={{ borderColor: "#C79A3F", color: "#8B5E10", background: "#fff" }}
            >
              Browse all recipes →
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="fp-recipes-cat-grid">
            {items.map((r) => <RecipeCard key={r.id} r={r} />)}
          </div>
        )}
      </section>

      {/* Sibling categories */}
      {siblings.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-16" data-testid="fp-recipes-cat-siblings">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: "#B25D38" }}>
            — More categories
          </div>
          <div className="flex flex-wrap gap-2">
            {siblings.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="text-xs px-3 py-1.5 rounded-full border hover:bg-white transition-colors"
                style={{ background: "#fff", borderColor: "#E9D8B4", color: "#6B4A16" }}
                data-testid={`fp-recipes-sibling-${s.to.split("/").pop()}`}
              >
                {s.label}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
